import { useEffect, useState } from "react";
import AppShell from "@/components/AppShell";
import { useAuth } from "@/context/AuthContext";
import { api, inr, formatErr } from "@/lib/api";
import { toast } from "sonner";
import {
  User as UserIcon, Mail, Phone, CreditCard, GraduationCap, Shield,
  Wallet, CheckCircle2, Clock, XCircle, ShoppingBag, ChevronRight,
} from "lucide-react";
import AdminUserStudentLists from "@/components/AdminUserStudentLists";

const TAGS = ["nuts", "dairy", "egg", "gluten", "soy", "shellfish", "spicy"];

export default function Profile() {
  const { user, school, refresh } = useAuth();
  const [children, setChildren] = useState([]);
  const [activeChild, setActiveChild] = useState(null);
  const [dietary, setDietary] = useState(null);
  const [dietaryLoading, setDietaryLoading] = useState(false);
  const [recentOrders, setRecentOrders] = useState([]);
  const [ordersLoading, setOrdersLoading] = useState(true);

  const isParent = user?.role === "parent";
  const isStudent = user?.role === "student";
  const canEdit = isParent; // only parent can edit dietary controls

  // Determine AppShell variant
  const variant = user?.role === "admin" ? "admin" : user?.role === "student" ? "student" : "parent";

  useEffect(() => {
    if (!user) return;
    loadOrders();
    if (isParent) {
      api.get("/parent/children").then(r => {
        setChildren(r.data || []);
        if (r.data?.length) setActiveChild(r.data[0].id);
      }).catch(() => {});
    } else if (isStudent) {
      // Student loads own dietary
      loadDietary(user.id);
    }
  }, [user]);

  useEffect(() => {
    if (activeChild) loadDietary(activeChild);
  }, [activeChild]);

  const loadDietary = async (studentId) => {
    setDietaryLoading(true);
    try {
      const { data } = await api.get(`/parental/restrictions/${studentId}`);
      setDietary(data.restrictions || {});
    } catch { setDietary({}); }
    setDietaryLoading(false);
  };

  const saveDietary = async () => {
    const targetId = isParent ? activeChild : user.id;
    if (!targetId) return;
    try {
      await api.put(`/parental/restrictions/${targetId}`, dietary);
      toast.success("Dietary controls saved");
    } catch (e) { toast.error(formatErr(e)); }
  };

  const loadOrders = async () => {
    setOrdersLoading(true);
    try {
      const { data } = await api.get("/orders/me");
      setRecentOrders((data || []).slice(0, 10));
    } catch { setRecentOrders([]); }
    setOrdersLoading(false);
  };

  const activeChildData = children.find(c => c.id === activeChild);

  return (
    <AppShell variant={variant}>
      <div className={`${user?.role === "admin" ? "max-w-[1400px]" : "max-w-4xl"} mx-auto px-4 sm:px-6 lg:px-8 py-10`}>
        {/* Header */}
        <div className="mb-8">
          <div className="text-xs font-bold uppercase tracking-[0.2em] text-brand">Profile</div>
          <h1 className="font-heading text-3xl sm:text-4xl font-extrabold tracking-tight text-ink mt-2">
            {user?.name || "My Account"}
          </h1>
        </div>

        {/* Profile Info Card */}
        <section data-testid="profile-info" className="bg-white rounded-2xl shadow-soft p-6 mb-6">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <InfoRow icon={UserIcon} label="Name" value={user?.name} testid="profile-name" />
            <InfoRow icon={Mail} label="Email" value={user?.email} testid="profile-email" />
            <InfoRow icon={Phone} label="Phone" value={user?.phone || "—"} testid="profile-phone" />
            <InfoRow icon={GraduationCap} label="Role" value={user?.role?.replace("_", " ")} testid="profile-role" />
            {isStudent && (
              <>
                <InfoRow icon={GraduationCap} label="Grade" value={user?.grade || "—"} testid="profile-grade" />
                <InfoRow icon={CreditCard} label="RFID Card" value={user?.rfid_card_id || "—"} testid="profile-rfid" />
              </>
            )}
            {(isParent || isStudent) && (
              <InfoRow icon={Wallet} label="Wallet" value={inr(user?.wallet_balance || 0)} testid="profile-wallet" />
            )}
            {school && (
              <InfoRow icon={Shield} label="School" value={school.name || school.slug} testid="profile-school" />
            )}
          </div>
        </section>

        {/* Admin-only: separate Users + Students lists */}
        {user?.role === "admin" && (
          <section className="mb-6">
            <AdminUserStudentLists />
          </section>
        )}

        {/* Dietary Controls */}
        {(isParent || isStudent) && (
          <section data-testid="dietary-section" className="bg-white rounded-2xl shadow-soft p-6 mb-6">
            <div className="flex items-center justify-between mb-4">
              <div>
                <div className="flex items-center gap-2 text-brand text-xs font-bold uppercase tracking-[0.2em]">
                  <Shield size={14} /> Dietary Controls
                </div>
                <p className="text-sm text-ink-500 mt-1">
                  {isParent ? "Set restrictions for your children. POS/QR will block violating items." : "View your dietary restrictions set by parent."}
                </p>
              </div>
            </div>

            {/* Child tabs (parent only) */}
            {isParent && children.length > 1 && (
              <div className="flex gap-2 mb-4 overflow-x-auto pb-1">
                {children.map(c => (
                  <button key={c.id} data-testid={`child-tab-${c.id}`}
                    onClick={() => setActiveChild(c.id)}
                    className={`px-4 py-2 rounded-full text-xs font-bold whitespace-nowrap transition-colors ${activeChild === c.id ? "bg-ink text-white" : "bg-cream-200 text-ink-700 hover:bg-cream-300"}`}>
                    {c.name}{c.grade ? ` · ${c.grade}` : ""}
                  </button>
                ))}
              </div>
            )}

            {isParent && activeChildData && (
              <div className="mb-4 p-3 bg-cream-100 rounded-xl text-sm text-ink-600">
                Editing for: <strong>{activeChildData.name}</strong>
                {activeChildData.grade && <> · {activeChildData.grade}</>}
                {activeChildData.rfid_card_id && <> · RFID: <code className="font-mono text-xs">{activeChildData.rfid_card_id}</code></>}
              </div>
            )}

            {dietaryLoading ? (
              <div className="text-ink-500 text-sm py-4">Loading dietary info…</div>
            ) : dietary && (
              <div className="space-y-4">
                {/* Allergens */}
                <div>
                  <label className="text-xs font-bold uppercase tracking-wider text-ink-500">Block items containing</label>
                  <div className="mt-2 flex flex-wrap gap-2">
                    {TAGS.map(t => {
                      const active = dietary.allergens?.includes(t);
                      return (
                        <button key={t} data-testid={`diet-allergen-${t}`}
                          onClick={() => {
                            if (!canEdit) return toast.error("Only parents can modify");
                            setDietary({ ...dietary, allergens: active ? dietary.allergens.filter(x => x !== t) : [...(dietary.allergens || []), t] });
                          }}
                          disabled={!canEdit}
                          className={`px-3 py-1.5 rounded-full text-xs font-bold uppercase border transition-colors ${active ? "bg-brand text-white border-brand" : "bg-white text-ink-700 border-border"} ${!canEdit ? "opacity-60 cursor-not-allowed" : ""}`}>
                          {t}
                        </button>
                      );
                    })}
                  </div>
                </div>

                {/* Veg/Vegan */}
                <div className="grid grid-cols-2 gap-3">
                  <label data-testid="diet-veg-only" className={`flex items-center gap-2 p-3 bg-cream-100 rounded-xl ${canEdit ? "cursor-pointer" : "cursor-not-allowed opacity-70"}`}>
                    <input type="checkbox" checked={!!dietary.veg_only} disabled={!canEdit}
                      onChange={e => setDietary({ ...dietary, veg_only: e.target.checked })} />
                    <span className="font-semibold text-sm">Vegetarian only</span>
                  </label>
                  <label data-testid="diet-vegan-only" className={`flex items-center gap-2 p-3 bg-cream-100 rounded-xl ${canEdit ? "cursor-pointer" : "cursor-not-allowed opacity-70"}`}>
                    <input type="checkbox" checked={!!dietary.vegan_only} disabled={!canEdit}
                      onChange={e => setDietary({ ...dietary, vegan_only: e.target.checked })} />
                    <span className="font-semibold text-sm">Vegan only</span>
                  </label>
                </div>

                {/* Junk limit */}
                <div>
                  <label className="text-xs font-bold uppercase tracking-wider text-ink-500">Junk-food limit (per week)</label>
                  <input data-testid="diet-junk-limit" type="number" min="0" placeholder="No limit"
                    value={dietary.junk_food_limit_per_week ?? ""}
                    onChange={e => {
                      if (!canEdit) return;
                      setDietary({ ...dietary, junk_food_limit_per_week: e.target.value ? Number(e.target.value) : null });
                    }}
                    disabled={!canEdit}
                    className="mt-1 w-full rounded-xl border border-border px-4 py-2.5 focus:border-brand focus:outline-none focus:ring-2 focus:ring-brand/20 disabled:opacity-60" />
                </div>

                {/* Blocked categories */}
                <div>
                  <label className="text-xs font-bold uppercase tracking-wider text-ink-500">Blocked categories (comma-separated)</label>
                  <input data-testid="diet-blocked-cats" placeholder="Beverages, Desserts"
                    value={(dietary.blocked_categories || []).join(", ")}
                    onChange={e => {
                      if (!canEdit) return;
                      setDietary({ ...dietary, blocked_categories: e.target.value.split(",").map(s => s.trim()).filter(Boolean) });
                    }}
                    disabled={!canEdit}
                    className="mt-1 w-full rounded-xl border border-border px-4 py-2.5 focus:border-brand focus:outline-none focus:ring-2 focus:ring-brand/20 disabled:opacity-60" />
                </div>

                {/* Save button */}
                {canEdit && (
                  <div className="flex justify-end pt-2">
                    <button data-testid="diet-save" onClick={saveDietary}
                      className="px-5 py-2.5 rounded-full bg-brand text-white font-semibold hover:bg-brand-600 active:scale-95 inline-flex items-center gap-2">
                      <CheckCircle2 size={14} /> Save controls
                    </button>
                  </div>
                )}

                {isStudent && (
                  <p className="text-xs text-ink-400 italic mt-2">
                    These restrictions are managed by your parent. Contact them to make changes.
                  </p>
                )}
              </div>
            )}
          </section>
        )}

        {/* Recent Orders */}
        <section data-testid="recent-orders-section" className="bg-white rounded-2xl shadow-soft p-6">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2 text-brand text-xs font-bold uppercase tracking-[0.2em]">
              <ShoppingBag size={14} /> Recent Orders
            </div>
          </div>

          {ordersLoading ? (
            <div className="text-ink-500 text-sm py-4">Loading orders…</div>
          ) : recentOrders.length === 0 ? (
            <div className="text-center py-8 text-ink-400">
              <ShoppingBag size={32} className="mx-auto mb-2 opacity-40" />
              <p className="text-sm">No orders yet</p>
            </div>
          ) : (
            <div className="space-y-2">
              {recentOrders.map(order => (
                <div key={order.id} data-testid={`order-row-${order.id}`}
                  className="flex items-center justify-between p-3 rounded-xl bg-cream-50 hover:bg-cream-100 transition-colors">
                  <div className="flex items-center gap-3 min-w-0">
                    <StatusIcon status={order.status} />
                    <div className="min-w-0">
                      <div className="text-sm font-semibold text-ink truncate">
                        {order.items?.map(i => i.name || i.item_name).join(", ") || order.type}
                      </div>
                      <div className="text-xs text-ink-500">
                        {order.type} · {order.scheduled_date || order.created_at?.slice(0, 10) || "—"}
                      </div>
                    </div>
                  </div>
                  <div className="text-right ml-3 shrink-0">
                    <div className="text-sm font-bold text-ink">{inr(order.total || 0)}</div>
                    <div className={`text-[10px] font-bold uppercase ${statusColor(order.status)}`}>{order.status}</div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </section>
      </div>
    </AppShell>
  );
}

function InfoRow({ icon: Icon, label, value, testid }) {
  return (
    <div className="flex items-center gap-3" data-testid={testid}>
      <div className="p-2 rounded-lg bg-cream-100">
        <Icon size={16} className="text-ink-500" />
      </div>
      <div>
        <div className="text-[10px] font-bold uppercase tracking-wider text-ink-400">{label}</div>
        <div className="text-sm font-semibold text-ink capitalize">{value || "—"}</div>
      </div>
    </div>
  );
}

function StatusIcon({ status }) {
  if (status === "delivered" || status === "completed") return <CheckCircle2 size={16} className="text-emerald-600 shrink-0" />;
  if (status === "cancelled") return <XCircle size={16} className="text-red-500 shrink-0" />;
  return <Clock size={16} className="text-amber-500 shrink-0" />;
}

function statusColor(s) {
  if (s === "delivered" || s === "completed") return "text-emerald-700";
  if (s === "cancelled") return "text-red-600";
  return "text-amber-700";
}
